import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ThasActionEvent,
  ThasClientEnvironment,
  ThasEvent,
  ThasMediaEvent,
  ThasSearchEvent,
  ThasToolUseEvent,
} from '@thiememeulenhoff/analytics-service-client';
import {
  AnalyticsModule,
  AnalyticsService,
} from '@thiememeulenhoff/ng-analytics-service-client';
import { Event } from '@thiememeulenhoff/ng-sdk';
import {
  CLICK_EVENT,
  ClickEvent,
  MEDIA_EVENT,
  MediaEvent,
  NAVIGATE_EVENT,
  SEARCH_EVENT,
  SearchEvent,
  TOOL_USE_EVENT,
  ToolUseEvent,
  UserActivityEvent,
  UserInterface,
} from '@thiememeulenhoff/types';
import { environment } from '../../../environments/environment';
import { URI_PARAMS } from '../uri-params';
import { UserActivityService } from './user-activity.service';

@Injectable({
  providedIn: 'root',
})
export class ThasService {
  readonly #thasAnalyticsService = inject(AnalyticsService);

  readonly #userActivityService = inject(UserActivityService);

  readonly #activatedRoute = inject(ActivatedRoute);

  static GetConfiguredModule() {
    return AnalyticsModule.forRoot({
      token: environment.thas.token,
      environment: environment.thas.environment as ThasClientEnvironment,
    });
  }

  initialize() {
    this.#userActivityService.on(NAVIGATE_EVENT, () => {
      this.#activatedRoute.root.firstChild?.paramMap.subscribe((params) => {
        this.#thasAnalyticsService.setArrangementCode(
          params.get(URI_PARAMS.arrangementCode) || '',
        );
      });
    });

    this.#userActivityService.on(CLICK_EVENT, (event) => {
      const { trigger, data } = event.data as ClickEvent;

      this.sendEvent(new ThasActionEvent(trigger, JSON.stringify(data)));
    });

    this.#userActivityService.on(MEDIA_EVENT, (event) => {
      const { action, type, target } = event.data as MediaEvent;

      this.sendEvent(new ThasMediaEvent(type, action, target));
    });

    this.#userActivityService.on(TOOL_USE_EVENT, (event) => {
      const { target, action } = event.data as ToolUseEvent;

      this.sendEvent(new ThasToolUseEvent(target, action));
    });

    this.#userActivityService.on(SEARCH_EVENT, this.#onSearch.bind(this));
  }

  setActor(user: UserInterface) {
    const userIsTeacher = user.roles?.includes(
      'thiememeulenhoff/license/teacher',
    );

    this.#thasAnalyticsService.setActor({
      actor: user.identifier,
      role: userIsTeacher ? 'teacher' : 'student',
    });
  }

  sendEvent(event: ThasEvent) {
    this.#thasAnalyticsService.send(event);
  }

  #onSearch(event: Event<UserActivityEvent>) {
    const { trigger, data } = event.data as SearchEvent;

    const query = Object.getOwnPropertyNames(data).includes('searchString')
      ? (data as { searchString: string }).searchString
      : '';

    this.sendEvent(new ThasSearchEvent(trigger, query));
  }
}
